import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { applyBrandColor } from "../lib/brand-context";
import { Card, CardContent } from "../components/ui/card";
import ThemeToggle from "../components/theme/ThemeToggle";
import {
  buildPublicDemoPageContent,
  getPublicThankYouProject,
} from "./publicPageContent";

export default function PublicThankYouPage() {
  const { formKey } = useParams();
  const location = useLocation();
  const [tenantInfo, setTenantInfo] = useState(location.state?.tenantInfo || null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (tenantInfo) {
      if (tenantInfo.branding_color) applyBrandColor(tenantInfo.branding_color);
      setLoadError(false);
      return;
    }
    let active = true;
    api
      .get("/api/public/tenant-info", { params: { form_key: formKey } })
      .then((response) => {
        if (!active) return;
        setTenantInfo(response.data);
        setLoadError(false);
        if (response.data?.branding_color) applyBrandColor(response.data.branding_color);
      })
      .catch(() => {
        if (!active) return;
        setLoadError(true);
      });
    return () => {
      active = false;
    };
  }, [formKey, tenantInfo]);

  const project = getPublicThankYouProject(tenantInfo);
  const content = buildPublicDemoPageContent(project, tenantInfo?.public_form);

  return (
    <div className="min-h-screen relative overflow-hidden bg-background text-foreground flex items-center justify-center px-4">
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-50" aria-hidden />
      <div className="pointer-events-none absolute inset-0 bg-radial-brand" aria-hidden />

      <div className="absolute top-4 right-4 z-30">
        <ThemeToggle />
      </div>

      <Card className="max-w-lg w-full backdrop-blur-xl relative z-10" data-testid="public-demo-thankyou">
        <CardContent className="p-10 text-center">
          <div
            className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-primary-foreground brand-glow-strong"
            style={{ background: "var(--brand)" }}
          >
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="font-display text-3xl font-semibold mt-4 tracking-tight">
            {content.thankYouTitle}
          </h1>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            {content.thankYouBody}
          </p>
          {loadError ? (
            <p className="text-xs text-muted-foreground mt-4" data-testid="public-demo-thankyou-fallback">
              Die Detailansicht Ihrer Formularseite konnte gerade nicht vollständig geladen
              werden. Ihre Anfrage wurde trotzdem übermittelt.
            </p>
          ) : null}
          <Link
            to={`/demo/${formKey}`}
            className="inline-flex items-center text-sm mt-8 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> {content.thankYouCta}
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
